/**
 * useChatSidebarFilters
 * 
 * Hook para gerenciar filtros da sidebar de chat.
 * Princípios: Single Responsibility, DRY
 * 
 * Refatorado: Suporte a aba 'favorites' e filtro por role
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { ChatConversation } from './useChat';
import { TabType } from '../components/SidebarTabs';
import { usePermissions } from '../../../hooks/usePermissions';

interface UseChatSidebarFiltersOptions {
    conversations: ChatConversation[];
    currentUserId: string | null | undefined;
    /** IDs das conversas favoritas (para aba favorites) */
    favoriteIds?: Set<string>;
}

type InboxAgentFilter = {
    assignedUserIds: string[];
    channelConnectionId: string | null;
};

function readInboxAgentFilter(value: InboxAgentFilter | string[]): InboxAgentFilter {
    if (Array.isArray(value)) {
        return { assignedUserIds: value, channelConnectionId: null };
    }
    return value;
}

export const useChatSidebarFilters = (
    conversations: ChatConversation[],
    currentUserId: string | null | undefined,
    favoriteIds?: Set<string>
) => {
    const { canViewAllConversations } = usePermissions();
    const tabInitializedRef = useRef(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<TabType>('mine');

    const [showArchived, setShowArchived] = useState(false);
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [inboxAgentFilter, setInboxAgentFilter] = useState<InboxAgentFilter | string[]>({
        assignedUserIds: [],
        channelConnectionId: null,
    });
    const { assignedUserIds, channelConnectionId } = readInboxAgentFilter(inboxAgentFilter);
    const setAssignedUserIds = (ids: string[]) => {
        setInboxAgentFilter((current) => ({
            ...readInboxAgentFilter(current),
            assignedUserIds: ids,
        }));
    };
    const setChannelConnectionId = (next: string | null) => {
        setInboxAgentFilter((current) => ({
            ...readInboxAgentFilter(current),
            channelConnectionId: next,
        }));
    };

    useEffect(() => {
        if (tabInitializedRef.current) return;
        tabInitializedRef.current = true;

        const saved = localStorage.getItem('chat_active_tab') as TabType;

        if (canViewAllConversations) {
            const adminTab =
                saved === 'unassigned' || saved === 'all' || saved === 'mine' ? saved : 'all';
            setActiveTab(adminTab);
            return;
        }

        // Member: Não atribuído | Meus | Favoritos
        const memberTab =
            saved === 'mine' || saved === 'unassigned' || saved === 'favorites' ? saved : 'mine';
        setActiveTab(memberTab);
    }, [canViewAllConversations]);

    useEffect(() => {
        if (!canViewAllConversations && activeTab === 'all') {
            setActiveTab('mine');
        }
    }, [canViewAllConversations, activeTab]);

    useEffect(() => {
        localStorage.setItem('chat_active_tab', activeTab);
    }, [activeTab]);

    const handleArchiveToggle = () => setShowArchived((prev) => !prev);
    const handleUnreadToggle = () => setShowUnreadOnly((prev) => !prev);

    const filteredConversations = useMemo(() => {
        let filtered = conversations;

        // Filtrar por status (ativas ou encerradas)
        if (showArchived) {
            filtered = filtered.filter(c => c.status === 'closed');
        } else {
            filtered = filtered.filter(c => c.status !== 'closed');
        }

        // Defense-in-depth: member nunca vê atribuídas a OUTRO member
        if (!canViewAllConversations) {
            filtered = filtered.filter(
                c => !c.assignedTo || (!!currentUserId && c.assignedTo === currentUserId)
            );
        }

        if (activeTab === 'mine') {
            filtered = filtered.filter(c => !!currentUserId && c.assignedTo === currentUserId);
        } else if (activeTab === 'unassigned') {
            filtered = filtered.filter(c => !c.assignedTo);
        } else if (activeTab === 'favorites') {
            if (favoriteIds && favoriteIds.size > 0) {
                filtered = filtered.filter(c => favoriteIds.has(c.id));
            } else {
                filtered = [];
            }
        } else if (activeTab === 'all' && canViewAllConversations) {
            // Owner/admin: todas as conversas abertas/encerradas (sem filtro de atribuição)
        }

        // Filtrar por busca
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(conv =>
                conv.name.toLowerCase().includes(lowerTerm) ||
                conv.id.includes(lowerTerm)
            );
        }

        if (showUnreadOnly) {
            filtered = filtered.filter((c) => (c.unreadCount || 0) > 0);
        }

        if (assignedUserIds.length > 0) {
            filtered = filtered.filter(
                (c) => c.assignedTo && assignedUserIds.includes(c.assignedTo),
            );
        }

        // Órfãs (connectionId null) permanecem visíveis em qualquer canal filtrado.
        if (channelConnectionId) {
            filtered = filtered.filter(
                (c) => !c.connectionId || c.connectionId === channelConnectionId,
            );
        }

        return [...filtered].sort((a, b) => {
            const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
            const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
            return timeB - timeA;
        });
    }, [conversations, searchTerm, activeTab, currentUserId, showArchived, showUnreadOnly, assignedUserIds, channelConnectionId, favoriteIds, canViewAllConversations]);

    return {
        searchTerm, setSearchTerm,
        activeTab, setActiveTab,
        showArchived, setShowArchived,
        showUnreadOnly, setShowUnreadOnly,
        assignedUserIds, setAssignedUserIds,
        channelConnectionId, setChannelConnectionId,
        filteredConversations,
        handleArchiveToggle,
        handleUnreadToggle,
    };
};
